import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { flagEnabled, useAuth } from "../../hooks/useAuth";
import { useUnreviewedSupportCount } from "../../hooks/useAdminSupportMessages";
import { type AdminTab, unreviewedBadge, visibleAdminNav } from "../../lib/adminNav";
import { prefetchAdminTab, prefetchAllAdminTabs } from "../../lib/adminPrefetch";
import styles from "./AdminLayout.module.css";

export const AdminLayout = (): JSX.Element => {
  const { user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const groups = useMemo(() => visibleAdminNav(user?.features), [user?.features]);
  const visibleTabs = groups.flatMap((group) => group.tabs);
  const current = visibleTabs.find((tab) => location.pathname.includes(`/admin/${tab.to}`));

  const badge = unreviewedBadge(useUnreviewedSupportCount());

  // Idle sweep warms every tab's default view shortly after the admin area
  // mounts; hovering/focusing a tab tops it up so the data at click time is
  // at most PREFETCH_FRESH_TTL_MS old. Gated tabs are skipped (their endpoints 404).
  const prefetchKey = visibleTabs
    .filter((tab) => tab.dataFlag === undefined || flagEnabled(user?.features, tab.dataFlag))
    .map((tab) => tab.to)
    .join(",");
  useEffect(() => {
    prefetchAllAdminTabs(prefetchKey.split(","));
  }, [prefetchKey]);

  const prefetchIfEnabled = (tab: AdminTab): void => {
    if (tab.dataFlag !== undefined && !flagEnabled(user?.features, tab.dataFlag)) return;
    prefetchAdminTab(tab.to);
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className={styles.page}>
      <div className={styles.body}>
        <button
          type="button"
          className={styles.menuToggle}
          aria-expanded={menuOpen}
          aria-controls="admin-sections"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span>{current?.label ?? "Sections"}</span>
          <span className={menuOpen ? `${styles.caret} ${styles.caretOpen}` : styles.caret} aria-hidden>
            ▼
          </span>
        </button>

        <nav
          id="admin-sections"
          className={menuOpen ? `${styles.sidebar} ${styles.sidebarOpen}` : styles.sidebar}
          aria-label="Admin sections"
        >
          {groups.map((group) => (
            <div key={group.label} className={styles.group}>
              <span className={styles.groupLabel}>{group.label}</span>
              {group.tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  className={({ isActive }) => (isActive ? `${styles.link} ${styles.linkActive}` : styles.link)}
                  onMouseEnter={() => prefetchIfEnabled(tab)}
                  onFocus={() => prefetchIfEnabled(tab)}
                >
                  {tab.label}
                  {tab.to === "support" && badge !== null && (
                    <span className={styles.badge} data-testid="support-unreviewed-count">
                      {badge}
                      <span className={styles.srOnly}> unreviewed</span>
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.panel}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};
